import { getOrdinalSuffix } from '~/helpers/ordinalSuffix';

/** Full English day names used by the automation UI (Sunday-first, matching JobsRepeat.DAYS). */
const FULL_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Legacy backend numeric byday codes are Monday-first ('0' = Monday).
 * This is not JavaScript's getDay() (Sunday = 0) and must not be "fixed"
 * without a backend contract change.
 */
const NUMERIC_DAY_MAP: Record<string, string> = {
  '0': 'Monday',
  '1': 'Tuesday',
  '2': 'Wednesday',
  '3': 'Thursday',
  '4': 'Friday',
  '5': 'Saturday',
  '6': 'Sunday',
};

const TWO_LETTER_DAY_MAP: Record<string, string> = {
  mo: 'Monday',
  tu: 'Tuesday',
  we: 'Wednesday',
  th: 'Thursday',
  fr: 'Friday',
  sa: 'Saturday',
  su: 'Sunday',
};

const THREE_LETTER_DAY_MAP: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export type AutomationFreq = 'daily' | 'weekly' | 'monthly';

/**
 * Normalize backend freq for the automation form.
 * Legacy numeric codes: '2' weekly, '3' monthly; anything else (including '1') is daily.
 */
export function normalizeFreq(freq?: string): AutomationFreq {
  if (!freq) {
    return 'daily';
  }

  if (freq === 'daily' || freq === 'weekly' || freq === 'monthly') {
    return freq;
  }

  const normalized: AutomationFreq =
    freq === '2' ? 'weekly' : freq === '3' ? 'monthly' : 'daily';

  if (freq !== normalized) {
    console.warn(
      `[AutomationForm] 🔄 Normalized freq: "${freq}" → "${normalized}"`
    );
  }

  return normalized;
}

/**
 * Normalize freq for read-only automation display.
 * Legacy numeric codes map 1/2/3; unknown strings are preserved.
 */
export function normalizeFreqForDisplay(freq: string | undefined): string {
  const freqMap: Record<string, string> = {
    '1': 'daily',
    '2': 'weekly',
    '3': 'monthly',
  };
  return freqMap[freq || '1'] || freq || 'daily';
}

/**
 * Normalize weekly byday values to full English day names.
 * Accepts full names, numeric Monday-first codes, and 2/3-letter codes.
 */
export function normalizeByDay(
  byday: string[] | null | undefined
): string[] | null {
  if (!byday || byday.length === 0) return null;

  return byday.map((day) => {
    if ((FULL_DAY_NAMES as readonly string[]).includes(day)) return day;
    const lc = day.trim().toLowerCase();
    if (NUMERIC_DAY_MAP[lc]) {
      const converted = NUMERIC_DAY_MAP[lc];
      console.warn(
        `[AutomationForm] 🔄 Normalized byday (numeric): "${day}" → "${converted}"`
      );
      return converted;
    }
    if (TWO_LETTER_DAY_MAP[lc]) {
      return TWO_LETTER_DAY_MAP[lc];
    }
    if (THREE_LETTER_DAY_MAP[lc]) {
      return THREE_LETTER_DAY_MAP[lc];
    }
    return day;
  });
}

type RepeatLike = {
  bymonthday?: string[] | null;
  byday?: string[] | null;
};

/**
 * Map backend monthly repeat fields to the form's single-select UI value.
 * `-1` weekday tokens become week `5` (last), matching the monthly selector.
 */
export function parseMonthlyRepeatToUiSelector(
  repeat: RepeatLike | null | undefined
): string | null {
  if (!repeat) return null;

  if (repeat.bymonthday?.length) {
    return `bymonthday:${repeat.bymonthday[0]}`;
  }

  if (repeat.byday?.length) {
    const raw = String(repeat.byday[0]).trim().toLowerCase();
    const match = raw.match(/^(-?\d+)([a-z]{2})$/);
    if (match) {
      const weekNum = parseInt(match[1], 10);
      const n = weekNum === -1 ? 5 : Math.abs(weekNum);
      const code = match[2];
      const dayName = TWO_LETTER_DAY_MAP[code] ?? 'Sunday';
      return `byday:${n},${dayName}`;
    }
  }

  return null;
}

/**
 * Formats a monthly pattern value (e.g. "byday:1,Thursday" or "bymonthday:15")
 * into a display label. Week `5` is treated as last.
 */
export function formatMonthlyPatternValue(
  value: string | null | undefined
): string | null {
  if (!value) return null;

  if (value.startsWith('bymonthday:')) {
    const day = value.substring('bymonthday:'.length);
    return `Day ${day} of the month`;
  }

  if (value.startsWith('byday:')) {
    const raw = value.substring('byday:'.length);
    const [nStr, dayName] = raw.split(',');
    const n = parseInt(nStr, 10);

    if (isNaN(n) || !dayName) return value;

    if (n === 5) {
      return `Last ${dayName} of the month`;
    }

    const ordinal = getOrdinalSuffix(n);
    return `${ordinal} ${dayName} of the month`;
  }

  return value;
}

/**
 * Formats monthly repeat patterns from backend arrays for the automation view.
 * `-1` weekday tokens become "Last"; `5th` stays "5th" (not last).
 */
export function formatMonthlyRepeat(
  bymonthday: string[] | null | undefined,
  byday: string[] | null | undefined
): string | null {
  if (bymonthday && bymonthday.length > 0) {
    const day = bymonthday[0];
    return `Day ${day} of the month`;
  }

  if (byday && byday.length > 0) {
    const raw = String(byday[0]).trim().toLowerCase();
    const match = raw.match(/^(-?\d+)([a-z]{2})$/);

    if (match) {
      const weekNum = parseInt(match[1], 10);
      const dayCode = match[2];
      const dayName = TWO_LETTER_DAY_MAP[dayCode] || 'Sunday';

      if (weekNum === -1) {
        return `Last ${dayName} of the month`;
      }

      const ordinal = getOrdinalSuffix(Math.abs(weekNum));
      return `${ordinal} ${dayName} of the month`;
    }

    return byday.join(', ');
  }

  return null;
}

type TaskWithSpecificAssets = {
  asset?: {
    selection?: string | null;
    assets?: readonly unknown[] | null;
  } | null;
};

/**
 * Collect unique specific-asset IDs from automation template tasks.
 */
export function extractSpecificAssetIds(
  tasks: TaskWithSpecificAssets[] | null | undefined
): string[] {
  if (!tasks) return [];

  const assetIds = tasks
    .map((task) => {
      if (task.asset?.selection === 'specific' && task.asset.assets) {
        return task.asset.assets;
      }
      return [];
    })
    .flat()
    .filter((id): id is string => Boolean(id));

  return Array.from(new Set(assetIds));
}
